//#region Imports
import i18next from 'i18next';
import { useTour } from '@reactour/tour';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import { AddModeratorOutlined } from '@mui/icons-material';
import { Tooltip, IconButton, Stack } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { DeleteOutlineOutlined as DeleteIcon } from '@mui/icons-material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';

import ModalU from '../Modal/Create/ModalU';
import useUsersTour from '../../tours/usersTour';
import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { GlobalVariables } from '../../globalVar';
import ModalUUpdate from '../Modal/Update/ModalUUpdate';
import ModalUDelete from '../Modal/Delete/ModalUDelete';
import { useTableLocale } from '../../hooks/useTableLocale';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import { useUserPermissions } from '../../hooks/userPermissions';
//#endregion

export const UserTableAxios = () => {
  const userPermissions = useUserPermissions();

  // Config de hooks
  const [userData, setUserData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalUShow, setModalUShow] = useState(false);
  const [modalUDeleteShow, setModalUDeleteShow] = useState(false);
  const [modalUUpdateShow, setModalUUpdateShow] = useState(false);

  const { t } = useTranslation();

  const {
    userString,
    userFullNameString,
    userEmailString,
    userStatusString,
    addString,
    detailString,
    editString,
    deleteString,
    viewPermsString,
    noUsersString,
    viewTutorialString,
    createdAtString,
    createdByString,
    updatedAtString,
    updatedByString,
    notesString,
    jobString,
    techEntryDateString,
  } = useTranslationHook();

  const { setIsOpen, setSteps, setCurrentStep } = useTour();

  const usersTour = useUsersTour(setModalUShow);

  const locale = useTableLocale();

  useEffect(() => {
    // Check if usersTour has not been viewed
    const hasSeenTour = localStorage.getItem('usersTourViewed');

    if (!hasSeenTour) {
      setIsOpen(true);
      setSteps(usersTour);
      setCurrentStep(0);
      // usersTour viewed
      localStorage.setItem('usersTourViewed', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsOpen, setSteps]);

  const restartTour = () => {
    setIsOpen(true);
    setSteps(usersTour);
    setCurrentStep(0);
    localStorage.setItem('usersTourViewed', 'false');
  };

  useEffect(() => {
    const updateUserData = () => {
      setUserData((prevData) => {
        return prevData.map((user) => ({
          ...user,
          is_active: t(`${user.is_active}`),
        }));
      });
    };
    i18next.on('languageChanged', updateUserData);

    return () => {
      i18next.off('languageChanged', updateUserData);
    };
  }, [t]);

  const endpoint = `${GlobalVariables.getURL()}listAll`;

  const getData = async () => {
    setIsLoading(true);

    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });
      const userData = response.data.map((user) => {
        return {
          ...user,
          is_active: t(`${user.is_active}`),
        };
      });
      setUserData(userData);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const manageUserDelete = (userInfo) => {
    var userId = userInfo.id;
    localStorage.setItem('userIdToDelete', userId);

    var userName = userInfo.name;
    var userEmail = userInfo.email;
    var userCreatedDate = userInfo.created_at;

    localStorage.setItem('userNameToDelete', userName);
    localStorage.setItem('userEmailToDelete', userEmail);
    localStorage.setItem('userCreatedDateToDelete', userCreatedDate);
  };

  const manageUserUpdate = (userInfo) => {
    return new Promise((resolve, reject) => {
      var userId = userInfo.id;
      localStorage.setItem('userIdToUpdate', userId);

      customAxios
        .get(`${GlobalVariables.getURL()}autofillUser/${userId}`, {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        })
        .then((response) => {
          var userData = response.data;
          var emailForUpdate = userData.user_info.email;
          localStorage.setItem('emailForUpdate', emailForUpdate);
          var nameForUpdate = userData.user_info.name;
          localStorage.setItem('nameForUpdate', nameForUpdate);
          var lastNameForUpdate = userData.last_names;
          localStorage.setItem('lastNameForUpdate', lastNameForUpdate);
          var roleIdForUpdate = userData.user_info.roles_id;
          localStorage.setItem('roleIdForUpdate', roleIdForUpdate);
          var roleNameForUpdate = userData.user_info.role_name;
          localStorage.setItem('roleNameForUpdate', roleNameForUpdate);
          var userJobForUpdate = userData.user_info.job;
          localStorage.setItem('userJobForUpdate', userJobForUpdate);
          var userEntryDateForUpdate = userData.user_info.entry_date;
          localStorage.setItem(
            'userEntryDateForUpdate',
            userEntryDateForUpdate,
          );
          var userNotesForUpdate = userData.user_info.notes;
          localStorage.setItem('userNotesForUpdate', userNotesForUpdate);
          var clientsForUpdate = userData.user_info.clients;
          localStorage.setItem('clientsForUpdate', clientsForUpdate);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  };

  const actionColumn = [
    {
      field: 'action',
      headerName: detailString,
      headerClassName: 'users-datagrid-actions-tour',
      width: 160,
      renderCell: (params) => {
        return (
          <div className="cellAction users-datagrid-actions-tour">
            <Tooltip title={viewPermsString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => navigate(`/users/permissions/${params.row.id}`)}
              >
                <AddModeratorOutlined
                  className="detail-icons"
                  id="update-icon"
                />
              </button>
            </Tooltip>
            {userPermissions.includes('Update Users') && (
              <Tooltip title={editString} arrow>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginTop: '5px',
                  }}
                  onClick={async () => {
                    try {
                      await manageUserUpdate(params.row);
                      setModalUUpdateShow(true);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                >
                  <ModeEditOutlineOutlinedIcon
                    className="detail-icons"
                    id="update-icon"
                  />
                </button>
              </Tooltip>
            )}
            {userPermissions.includes('Delete Users') && (
              <Tooltip title={deleteString} arrow>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginTop: '5px',
                  }}
                  onClick={() => {
                    setModalUDeleteShow(true);
                    manageUserDelete(params.row);
                  }}
                >
                  <DeleteIcon className="detail-icons" id="delete-icon" />
                </button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  // Columnas
  const userColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'full_name',
      headerName: userFullNameString,
      width: 250,
      renderCell: (params) => {
        var full_name = params.row.name + ' ' + params.row.last_name;
        return (
          <div>
            <span>{full_name}</span>
          </div>
        );
      },
    },
    {
      field: 'email',
      headerName: userEmailString,
      width: 220,
    },
    {
      field: 'role_name',
      headerName: 'Rol',
      width: 150,
    },
    {
      field: 'job',
      headerName: jobString,
      width: 150,
    },
    {
      field: 'clients',
      headerName: 'Plazas',
      width: 250,
    },
    {
      field: 'entry_date',
      headerName: techEntryDateString,
      width: 150,
    },
    {
      field: 'is_active',
      headerName: userStatusString,
      width: 150,
      renderCell: (params) => {
        if (
          params.row.is_active === 'Activo' ||
          params.row.is_active === 'Active'
        ) {
          return (
            <div>
              <span className="statusActive">{params.row.is_active}</span>
            </div>
          );
        } else {
          return (
            <div>
              <span className="statusInactive">{params.row.is_active}</span>
            </div>
          );
        }
      },
      valueGetter: (params) => params.row.is_active,
    },
    {
      field: 'created_at',
      headerName: createdAtString,
      width: 150,
    },
    {
      field: 'created_by',
      headerName: createdByString,
      width: 150,
    },
    {
      field: 'updated_at',
      headerName: updatedAtString,
      width: 150,
    },
    {
      field: 'updated_by',
      headerName: updatedByString,
      width: 150,
    },
    {
      field: 'notes',
      headerName: notesString,
      width: 200,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );

  // Check if user has any action permissions
  const hasActionPermissions = true;

  return (
    <>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          {userPermissions.includes('Create Users') && (
            <button
              className="add-button create-user-tour"
              onClick={() => setModalUShow(true)}
            >
              {addString} {userString} <FontAwesomeIcon icon={faAdd} />
            </button>
          )}
        </div>
        {hasActionPermissions && (
          <Tooltip title={viewTutorialString}>
            <IconButton
              className="tour-button"
              size="medium"
              onClick={() => restartTour()}
            >
              <FontAwesomeIcon icon={faCircleInfo} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <ModalU
        show={modalUShow}
        onHide={() => setModalUShow(false)}
        getUsers={getData}
        className="create-user-form-tour"
      />

      <ModalUDelete
        show={modalUDeleteShow}
        onHide={() => setModalUDeleteShow(false)}
        getUsers={getData}
      />

      <ModalUUpdate
        show={modalUUpdateShow}
        onHide={() => setModalUUpdateShow(false)}
        getUsers={getData}
      />

      <ModalU getUsers={getData} />

      <DataGridPro
        pagination
        loading={isLoading}
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          pinnedColumns: hasActionPermissions ? { right: ['action'] } : {},
        }}
        rows={userData}
        columns={
          hasActionPermissions ? userColumns.concat(actionColumn) : userColumns
        }
        disableRowSelectionOnClick
        checkboxSelection
        slots={{
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {noUsersString}
            </Stack>
          ),
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            csvOptions: {
              utf8WithBom: true,
            },
          },
        }}
        className="users-datagrid-tour"
      />
    </>
  );
};
